import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { finalize } from 'rxjs';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  DynamicDefinitionBundle,
  DynamicDefinitionImportConflictStrategy,
  DynamicDefinitionImportPreview,
  DynamicDefinitionImportResult
} from './dynamic.models';
import { DynamicApiService, DynamicForm } from './dynamic-api.service';
import { DynamicEntity } from './dynamic.models';

@Component({
  standalone: true,
  selector: 'ain-dynamic-import-export',
  imports: [
    FormsModule,
    ButtonModule,
    CheckboxModule,
    SelectModule,
    TabsModule,
    TagModule,
    InfoHintComponent,
    AinPageComponent,
    AinStateComponent
  ],
  template: `
    <ain-page [title]="t('dynamic.importExport.title')" [description]="t('dynamic.importExport.description')">
      <ain-info-hint
        [titleKey]="'dynamic.importExport.infoTitle'"
        [descriptionKey]="'dynamic.importExport.infoDescription'" />

      <p-tabs value="export">
        <p-tablist>
          <p-tab value="export">{{ t('dynamic.importExport.export') }}</p-tab>
          <p-tab value="import">{{ t('dynamic.importExport.import') }}</p-tab>
        </p-tablist>
        <p-tabpanels>
          <p-tabpanel value="export">
          @if (loadingCatalog()) {
            <ain-state type="loading" [title]="t('common.loading')" />
          } @else {
            <div class="panel-section">
              <h3>{{ t('dynamic.importExport.selectForms') }}</h3>
              <ul class="check-list">
                @for (form of forms(); track form.id) {
                  <li>
                    <p-checkbox
                      [binary]="true"
                      [(ngModel)]="selectedFormIds[form.id]"
                      [inputId]="'form-' + form.id" />
                    <label [for]="'form-' + form.id">{{ form.name }}</label>
                  </li>
                }
              </ul>
            </div>

            <div class="panel-section">
              <h3>{{ t('dynamic.importExport.selectEntities') }}</h3>
              <ul class="check-list">
                @for (entity of entities(); track entity.id) {
                  <li>
                    <p-checkbox
                      [binary]="true"
                      [(ngModel)]="selectedEntityIds[entity.id]"
                      [inputId]="'entity-' + entity.id" />
                    <label [for]="'entity-' + entity.id">{{ entity.displayName || entity.name }}</label>
                  </li>
                }
              </ul>
            </div>

            <div class="panel-section inline-toggle">
              <p-checkbox
                [binary]="true"
                [(ngModel)]="includeDependencies"
                inputId="includeDependencies" />
              <label for="includeDependencies">{{ t('dynamic.importExport.includeDependencies') }}</label>
            </div>

            <button
              pButton
              type="button"
              icon="pi pi-download"
              [label]="t('dynamic.importExport.export')"
              [disabled]="exporting() || !hasExportSelection()"
              (click)="exportSelected()"></button>
          }
        </p-tabpanel>

        <p-tabpanel value="import">
          <div class="panel-section">
            <input #importFileInput type="file" accept="application/json,.json" hidden (change)="onImportFileSelected($event)" />
            <button
              pButton
              type="button"
              severity="secondary"
              icon="pi pi-upload"
              [label]="t('dynamic.importExport.upload')"
              (click)="importFileInput.click()"></button>
            @if (uploadedFileName()) {
              <span class="file-name">{{ uploadedFileName() }}</span>
            }
          </div>

          @if (preview()) {
            <div class="panel-section">
              <h3>{{ t('dynamic.importExport.previewDiff') }}</h3>
              <ul class="diff-list">
                @for (item of preview()!.items; track item.name + item.itemType) {
                  <li>
                    <p-tag
                      [severity]="item.status === 'New' ? 'success' : 'warn'"
                      [value]="item.status === 'New' ? t('dynamic.importExport.new') : t('dynamic.importExport.conflict')" />
                    <span>{{ item.itemType }} · {{ item.name }}</span>
                    @if (item.conflictDetail) {
                      <small>{{ item.conflictDetail }}</small>
                    }
                  </li>
                }
              </ul>
              @for (dep of preview()!.dependencies; track dep.fieldKey + dep.targetEntityKey) {
                @if (!dep.resolved) {
                  <p class="warn-line">{{ dep.fieldKey }} → {{ dep.targetEntityKey }}: {{ dep.resolutionDetail }}</p>
                }
              }
            </div>

            <div class="panel-section strategy-row">
              <label>{{ t('dynamic.importExport.apply') }}</label>
              <p-select
                [options]="conflictOptions"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="conflictStrategy"
                appendTo="body" />
              <button
                pButton
                type="button"
                [label]="t('dynamic.importExport.apply')"
                [disabled]="importing() || !parsedBundle()"
                (click)="applyImport()"></button>
            </div>
          }

          @if (importResult()) {
            <div class="panel-section">
              <h3>{{ t('dynamic.importExport.resultSummary') }}</h3>
              <ul class="diff-list">
                @for (item of importResult()!.items; track item.name + item.outcome) {
                  <li>
                    <strong>{{ item.itemType }} · {{ item.name }}</strong>
                    <span>{{ item.outcome }}</span>
                    @if (item.detail) {
                      <small>{{ item.detail }}</small>
                    }
                  </li>
                }
              </ul>
            </div>
          }
        </p-tabpanel>
        </p-tabpanels>
      </p-tabs>
    </ain-page>
  `,
  styles: [`
    .panel-section { margin: 16px 0; }
    .panel-section h3 { margin: 0 0 8px; font-size: 1rem; }
    .check-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 8px; }
    .check-list li { display: flex; align-items: center; gap: 8px; }
    .inline-toggle { display: flex; align-items: center; gap: 8px; }
    .diff-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 8px; }
    .diff-list li { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
    .diff-list small, .warn-line { color: var(--text-color-secondary); }
    .strategy-row { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; }
    .file-name { margin-inline-start: 12px; color: var(--text-color-secondary); }
  `]
})
export class DynamicImportExportComponent implements OnInit {
  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);

  readonly forms = signal<DynamicForm[]>([]);
  readonly entities = signal<DynamicEntity[]>([]);
  readonly loadingCatalog = signal(false);
  readonly exporting = signal(false);
  readonly importing = signal(false);
  readonly uploadedFileName = signal<string | null>(null);
  readonly parsedBundle = signal<DynamicDefinitionBundle | null>(null);
  readonly preview = signal<DynamicDefinitionImportPreview | null>(null);
  readonly importResult = signal<DynamicDefinitionImportResult | null>(null);

  selectedFormIds: Record<string, boolean> = {};
  selectedEntityIds: Record<string, boolean> = {};
  includeDependencies = true;
  conflictStrategy: DynamicDefinitionImportConflictStrategy = 'Skip';

  readonly conflictOptions = [
    { value: 'Skip' as const, label: '' },
    { value: 'Rename' as const, label: '' },
    { value: 'ReplaceDraft' as const, label: '' }
  ];

  ngOnInit(): void {
    this.conflictOptions[0].label = this.t('dynamic.importExport.conflict.skip');
    this.conflictOptions[1].label = this.t('dynamic.importExport.conflict.rename');
    this.conflictOptions[2].label = this.t('dynamic.importExport.conflict.replaceDraft');
    this.loadCatalog();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  loadCatalog(): void {
    this.loadingCatalog.set(true);
    this.api.listForms().subscribe({
      next: forms => {
        this.forms.set(forms);
        this.api.listEntities().subscribe({
          next: entities => {
            this.entities.set(entities);
            this.loadingCatalog.set(false);
          },
          error: () => this.loadingCatalog.set(false)
        });
      },
      error: () => this.loadingCatalog.set(false)
    });
  }

  hasExportSelection(): boolean {
    return Object.values(this.selectedFormIds).some(Boolean)
      || Object.values(this.selectedEntityIds).some(Boolean);
  }

  exportSelected(): void {
    const formIds = Object.entries(this.selectedFormIds).filter(([, v]) => v).map(([id]) => id);
    const entityIds = Object.entries(this.selectedEntityIds).filter(([, v]) => v).map(([id]) => id);
    this.exporting.set(true);
    this.api.exportDefinitionsAndDownload(formIds, entityIds, this.includeDependencies)
      .pipe(finalize(() => this.exporting.set(false)))
      .subscribe();
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) {
      return;
    }

    this.uploadedFileName.set(file.name);
    this.importResult.set(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const bundle = JSON.parse(String(reader.result ?? '{}')) as DynamicDefinitionBundle;
        this.parsedBundle.set(bundle);
        this.api.previewImport(bundle).subscribe({
          next: preview => this.preview.set(preview),
          error: () => this.preview.set(null)
        });
      } catch {
        this.parsedBundle.set(null);
        this.preview.set(null);
      }
    };
    reader.readAsText(file);
    input.value = '';
  }

  applyImport(): void {
    const bundle = this.parsedBundle();
    if (!bundle) {
      return;
    }

    this.importing.set(true);
    this.api.importDefinitions(bundle, this.conflictStrategy)
      .pipe(finalize(() => this.importing.set(false)))
      .subscribe({
        next: result => {
          this.importResult.set(result);
          this.loadCatalog();
        }
      });
  }
}
